import { useState } from "react";
import { X, CheckCircle2, Loader2, Sparkles, CreditCard, Zap, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";

/**
 * Credit packages — 3x2 grid (compact). 1 קרדיט = 0.6 ₪ retail
 * (matches client display rate — the business margin lives in the
 * 3× AI_COST_MULTIPLIER on work charges, not in retail markup).
 * Discounts: 2500 (10%) / 5000 (15%) / 10000 (20%).
 */
const RETAIL_PER_CREDIT = 0.6; // ILS
const SUPPORT_WA_NUMBER = "972504831000"; // Israeli — no leading 0/+, raw for wa.me

export const PRICING_TIERS = [
  { id: "t500",   credits: 500,   discount: 0  },
  { id: "t1000",  credits: 1000,  discount: 0  },
  { id: "t1500",  credits: 1500,  discount: 0  },
  { id: "t2500",  credits: 2500,  discount: 10, hot: true },
  { id: "t5000",  credits: 5000,  discount: 15, hot: true },
  { id: "t10000", credits: 10000, discount: 20, hot: true },
];

function tierPrice(tier) {
  const base = tier.credits * RETAIL_PER_CREDIT;
  const final = base * (1 - tier.discount / 100);
  return {
    base: Math.round(base),
    final: Math.round(final),
    saved: Math.round(base - final),
    perCredit: (final / tier.credits).toFixed(2),
  };
}

export default function CreditPricingPackages({ companyId, prefilledDebt = null, onClose }) {
  const [busyId, setBusyId] = useState(null);

  const buy = async (tier) => {
    if (!companyId) return;
    setBusyId(tier.id);
    try {
      const { final } = tierPrice(tier);
      const { data } = await api.post(`/payments/cardcom/create-checkout`, {
        company_id: companyId,
        credits: tier.credits,
        amount_ils: final,
        note: tier.discount > 0 ? `חבילה ${tier.credits} קרדיטים (${tier.discount}% הנחה)` : `חבילה ${tier.credits} קרדיטים`,
      });
      if (data?.url) { window.location.href = data.url; return; }
      toast.error("לא הצליח לפתוח דף תשלום");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "שגיאת תשלום");
    } finally {
      setBusyId(null);
    }
  };

  const payDebt = async () => {
    if (!companyId) return;
    setBusyId("debt");
    try {
      const { data } = await api.post(`/companies/${companyId}/credits/pay-debt`);
      if (data?.url) { window.location.href = data.url; return; }
      toast.error("לא הצליח לפתוח דף תשלום חוב");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "שגיאה");
    } finally { setBusyId(null); }
  };

  const openBankTransferWA = () => {
    const msg = encodeURIComponent("שלום, אני מעוניין/ת לשלם בהעברה בנקאית עבור FlowUp. אשמח להנחיות.");
    window.open(`https://wa.me/${SUPPORT_WA_NUMBER}?text=${msg}`, "_blank");
  };

  return (
    <div
      className="fixed inset-0 z-[10020] bg-black/80 backdrop-blur-md flex items-center justify-center p-3 sm:p-4 overflow-y-auto"
      onClick={onClose}
      data-testid="credit-pricing-backdrop"
    >
      <div
        className="relative w-full max-w-3xl rounded-2xl overflow-hidden shadow-2xl my-auto"
        onClick={(e) => e.stopPropagation()}
        dir="rtl"
        data-testid="credit-pricing-modal"
      >
        <div className="absolute inset-0 bg-gradient-to-br from-[#0a1429] via-[#0f1d3d] to-[#0a1429]" />
        <div className="absolute -top-16 -right-16 w-72 h-72 rounded-full bg-cyan-500/10 blur-3xl" />
        <div className="absolute -bottom-16 -left-16 w-72 h-72 rounded-full bg-blue-600/15 blur-3xl" />

        <div className="relative p-4 sm:p-5">
          {/* Compact header */}
          <div className="flex items-start justify-between gap-3 mb-4">
            <div className="min-w-0">
              <div className="inline-flex items-center gap-1 text-[9px] font-extrabold uppercase tracking-widest text-cyan-300 bg-cyan-500/10 border border-cyan-500/30 rounded-full px-2 py-0.5 mb-1.5">
                <Sparkles className="h-2.5 w-2.5" /> חבילות קרדיטים
              </div>
              <h2 className="font-heading text-lg sm:text-xl font-extrabold bg-gradient-to-l from-white via-cyan-100 to-cyan-300 bg-clip-text text-transparent leading-tight">
                בחר/י חבילה — הקרדיטים נטענים מיידית
              </h2>
              <p className="text-[11px] text-slate-400 mt-0.5">
                1 קרדיט = <strong className="text-cyan-200">{RETAIL_PER_CREDIT}₪</strong> · CardCom מאובטח
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="h-8 w-8 rounded-lg bg-white/[0.04] hover:bg-white/[0.08] border border-white/10 text-slate-300 inline-flex items-center justify-center transition shrink-0"
              data-testid="credit-pricing-close"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {/* Debt banner */}
          {prefilledDebt && prefilledDebt.in_debt && (
            <div className="mb-4 rounded-xl border border-rose-500/40 bg-gradient-to-l from-rose-950/60 to-rose-900/40 p-3 flex flex-col sm:flex-row items-stretch sm:items-center gap-2" data-testid="pay-debt-banner">
              <div className="flex-1 min-w-0">
                <div className="text-[9px] uppercase tracking-widest font-bold text-rose-300">יתרת חוב</div>
                <div className="text-lg sm:text-xl font-extrabold text-rose-100 tabular-nums leading-tight">
                  {prefilledDebt.owed_nis.toFixed(2)} ₪
                  <span className="text-[11px] font-bold text-rose-300/80 mr-1.5">
                    ({prefilledDebt.owed_credits.toFixed(0)} קרדיטים)
                  </span>
                </div>
              </div>
              <button
                type="button"
                onClick={payDebt}
                disabled={busyId === "debt"}
                className="shrink-0 inline-flex items-center justify-center gap-1.5 px-3.5 py-2 rounded-lg bg-gradient-to-l from-rose-500 to-rose-600 hover:from-rose-400 hover:to-rose-500 text-white font-extrabold text-xs shadow-md shadow-rose-500/30 disabled:opacity-50 transition"
                data-testid="pay-debt-cta"
              >
                {busyId === "debt"
                  ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  : <><CreditCard className="h-3.5 w-3.5" /> שלם חוב {prefilledDebt.owed_nis.toFixed(0)}₪</>}
              </button>
            </div>
          )}

          {/* 3x2 Grid — compact cards */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2 sm:gap-2.5">
            {PRICING_TIERS.map((tier) => {
              const p = tierPrice(tier);
              const isHot = !!tier.hot;
              return (
                <button
                  key={tier.id}
                  type="button"
                  disabled={busyId === tier.id}
                  onClick={() => buy(tier)}
                  className={`group relative text-right rounded-xl p-3 border transition overflow-hidden ${
                    isHot
                      ? "bg-gradient-to-br from-blue-700/30 via-blue-800/20 to-cyan-700/20 border-cyan-400/40 hover:border-cyan-300"
                      : "bg-white/[0.03] border-white/10 hover:border-cyan-400/40 hover:bg-white/[0.06]"
                  } disabled:opacity-50`}
                  data-testid={`pricing-tier-${tier.credits}`}
                >
                  {tier.discount > 0 && (
                    <span className="absolute top-2 left-2 inline-flex items-center gap-0.5 text-[9px] font-extrabold bg-gradient-to-l from-amber-400 to-amber-500 text-stone-900 rounded-full px-1.5 py-0.5 shadow">
                      <Zap className="h-2 w-2" />
                      {tier.discount}%
                    </span>
                  )}

                  <div className="text-[9px] uppercase tracking-widest font-bold text-cyan-300/70 mt-0.5">קרדיטים</div>
                  <div className="font-heading text-2xl sm:text-3xl font-extrabold text-white tabular-nums leading-none mt-0.5">
                    {tier.credits.toLocaleString("he-IL")}
                  </div>

                  <div className="mt-2 flex items-baseline gap-1.5 flex-wrap">
                    <span className="text-lg font-extrabold text-cyan-200 tabular-nums">{p.final.toLocaleString("he-IL")}₪</span>
                    {tier.discount > 0 && (
                      <span className="text-[10px] text-slate-500 line-through tabular-nums">{p.base.toLocaleString("he-IL")}₪</span>
                    )}
                  </div>
                  <div className="text-[9px] text-slate-400 mt-0.5 flex items-center justify-between gap-1">
                    <span>{p.perCredit}₪ / קרדיט</span>
                    {tier.discount > 0 && (
                      <span className="text-emerald-300 font-bold">חוסך {p.saved}₪</span>
                    )}
                  </div>

                  <div className="mt-2 inline-flex items-center justify-center w-full gap-1 py-1.5 rounded-lg bg-gradient-to-l from-blue-700 to-cyan-500 text-white font-extrabold text-[11px] shadow group-hover:shadow-cyan-500/30 transition">
                    {busyId === tier.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><CheckCircle2 className="h-3 w-3" /> רכישה</>}
                  </div>
                </button>
              );
            })}
          </div>

          {/* Bank transfer → WhatsApp agent CTA */}
          <button
            type="button"
            onClick={openBankTransferWA}
            className="mt-3 w-full inline-flex items-center justify-center gap-2 py-2.5 rounded-xl bg-gradient-to-l from-emerald-600 to-emerald-500 hover:from-emerald-500 hover:to-emerald-400 text-white font-extrabold text-xs shadow-md shadow-emerald-500/20 transition"
            data-testid="bank-transfer-whatsapp"
          >
            <MessageCircle className="h-4 w-4" />
            לתשלום בהעברה בנקאית — שלח/י וואצאפ לנציג
          </button>

          <div className="mt-2 text-center text-[10px] text-slate-500">
            🔒 חיוב מאובטח דרך CardCom · כרטיס אשראי / ביט / PayPal · חשבונית אוטומטית
          </div>
        </div>
      </div>
    </div>
  );
}
